<template>
  <expandable header="Video Consent" class="mb-10">
    <b-row>
      <b-col cols="12" md="4">
        <file-input-group
          v-if="!hasWebcamRecording"
          v-model.trim="form.video_consent_document"
          label="Video Consent Recording"
          :disabled="isDisabled"
          accept=".mp4,.mov,.webm,.pdf,video/*"
          @input="onInput"
          rules="required"
        ></file-input-group>
        <div v-else class="mb-3">
          <label>Video Consent Recording <span style="color:red">*</span></label>
          <div class="d-flex align-items-center mt-1">
            <span class="text-success mr-2">&#10003;</span>
            <span class="small text-truncate" style="max-width: 200px;">{{ form.video_consent_document.name }}</span>
            <b-button
              variant="danger"
              size="sm"
              class="ml-2"
              :disabled="isDisabled"
              @click="clearWebcamRecording"
            >
              Change
            </b-button>
          </div>
        </div>
      </b-col>
      <b-col cols="12" md="4">
        <date-time-picker
          v-model.trim="form.video_consent_date"
          label="Video Consent Date & Time"
          :disabled="isDisabled"
          @input="onInput"
          rules="required"
        ></date-time-picker>
      </b-col>
      <b-col cols="12" md="4">
        <radio-group
          v-model.trim="form.video_consent_status"
          label="Video Consent Status"
          :options="statusOptions"
          :disabled="isDisabled"
          @input="onInput"
          rules="required"
        ></radio-group>
      </b-col>
      <b-col cols="12" md="8">
        <text-area
          v-model.trim="form.consent_remarks"
          label="Consent Remarks"
          :disabled="isDisabled"
          @input="onInput"
          rules="max-500"
        ></text-area>
      </b-col>
    </b-row>
    <!-- Webcam Recording Option -->
    <b-row v-if="!isDisabled" class="mt-2">
      <b-col cols="12">
        <!-- Phase: idle - show record button -->
        <b-button
          v-if="phase === 'idle'"
          variant="outline-info"
          size="sm"
          @click="openCamera"
        >
          Record Video via Webcam
        </b-button>

        <!-- Phase: preview - camera open, not yet recording -->
        <!-- Phase: recording - actively recording -->
        <div v-if="phase === 'preview' || phase === 'recording'" class="webcam-container mt-2">
          <video
            ref="webcamPreview"
            autoplay
            muted
            playsinline
            class="webcam-video"
          ></video>
          <div class="mt-2">
            <b-button
              v-if="phase === 'preview'"
              variant="danger"
              size="sm"
              @click="beginRecord"
            >
              Start Recording
            </b-button>
            <b-button
              v-if="phase === 'recording'"
              variant="warning"
              size="sm"
              @click="stopRecord"
            >
              Stop Recording ({{ recordingTime }}s)
            </b-button>
            <b-button
              variant="secondary"
              size="sm"
              class="ml-2"
              @click="cancelWebcam"
            >
              Cancel
            </b-button>
          </div>
        </div>

        <!-- Phase: recorded - show playback (v-show keeps element in DOM for reliable ref access) -->
        <div v-show="phase === 'recorded'" class="mt-2">
          <video
            ref="recordedVideo"
            controls
            playsinline
            class="recorded-video"
          ></video>
          <div class="mt-2">
            <b-button
              variant="success"
              size="sm"
              @click="useRecording"
            >
              Use This Recording
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="ml-2"
              @click="discardRecording"
            >
              Discard
            </b-button>
          </div>
        </div>
      </b-col>
    </b-row>
  </expandable>
</template>

<script>
import Expandable from "../../Expandable.vue";
import FileInputGroup from "../../form/FileInputGroup.vue";
import DateTimePicker from "../../form/DateTimePicker.vue";
import RadioGroup from "../../form/RadioGroup.vue";
import TextArea from "../../form/TextArea.vue";
export default {
  props: {
    value: {},
    isDisabled: {
      default: false,
      type: Boolean,
    },
  },
  components: {
    Expandable,
    FileInputGroup,
    DateTimePicker,
    RadioGroup,
    TextArea,
  },
  data() {
    return {
      form: {},
      statusOptions: [
        { name: "Pending", value: "0" },
        { name: "Complete", value: "1" },
      ],
      // Single state: idle | preview | recording | recorded
      phase: "idle",
      recordedUrl: "",
      recordedBlob: null,
      recordedMimeType: "video/webm",
      mediaStream: null,
      mediaRecorder: null,
      recordedChunks: [],
      recordingTime: 0,
      recordingTimer: null,
    };
  },
  computed: {
    hasWebcamRecording() {
      return this.form.video_consent_document instanceof File;
    },
  },
  methods: {
    onInput() {
      this.$emit("input", this.form);
    },
    getSupportedMimeType() {
      var types = [
        "video/webm;codecs=vp8,opus",
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=h264,opus",
        "video/webm",
        "video/mp4",
      ];
      for (var i = 0; i < types.length; i++) {
        if (MediaRecorder.isTypeSupported(types[i])) {
          return types[i];
        }
      }
      return "";
    },
    async openCamera() {
      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        this.phase = "preview";
        this.$nextTick(function () {
          if (this.$refs.webcamPreview) {
            this.$refs.webcamPreview.srcObject = this.mediaStream;
          }
        });
      } catch (err) {
        this.$alert.show(
          "Unable to access camera. Please allow camera permission or use file upload instead."
        );
      }
    },
    beginRecord() {
      var vm = this;
      vm.recordedChunks = [];
      vm.recordingTime = 0;
      var mimeType = vm.getSupportedMimeType();
      vm.recordedMimeType = mimeType || "video/webm";
      try {
        var options = mimeType ? { mimeType: mimeType } : undefined;
        vm.mediaRecorder = new MediaRecorder(vm.mediaStream, options);
      } catch (e) {
        vm.mediaRecorder = new MediaRecorder(vm.mediaStream);
      }
      vm.mediaRecorder.ondataavailable = function (event) {
        if (event.data && event.data.size > 0) {
          vm.recordedChunks.push(event.data);
        }
      };
      vm.mediaRecorder.onstop = function () {
        var actualType = vm.mediaRecorder.mimeType || vm.recordedMimeType;
        vm.recordedMimeType = actualType;
        vm.recordedBlob = new Blob(vm.recordedChunks, { type: actualType });
        if (vm.recordedUrl) {
          URL.revokeObjectURL(vm.recordedUrl);
        }
        vm.recordedUrl = URL.createObjectURL(vm.recordedBlob);
        vm.stopStream();
        vm.phase = "recorded";
        // v-show keeps the video element in DOM, so ref is always available
        var video = vm.$refs.recordedVideo;
        if (video) {
          video.src = vm.recordedUrl;
          video.load();
        }
      };
      vm.mediaRecorder.start(1000);
      vm.phase = "recording";
      vm.recordingTimer = setInterval(function () {
        vm.recordingTime++;
      }, 1000);
    },
    stopRecord() {
      clearInterval(this.recordingTimer);
      if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
        this.mediaRecorder.stop();
      }
    },
    stopStream() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(function (track) {
          track.stop();
        });
        this.mediaStream = null;
      }
    },
    cancelWebcam() {
      clearInterval(this.recordingTimer);
      if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
        this.mediaRecorder.stop();
      }
      this.stopStream();
      this.cleanup();
      this.phase = "idle";
    },
    useRecording() {
      var ext = this.recordedMimeType.indexOf("mp4") !== -1 ? "mp4" : "webm";
      var file = new File(
        [this.recordedBlob],
        "video_consent_" + Date.now() + "." + ext,
        { type: this.recordedBlob.type || this.recordedMimeType }
      );
      this.form.video_consent_document = file;
      this.cleanup();
      this.phase = "idle";
      this.onInput();
    },
    discardRecording() {
      this.cleanup();
      this.phase = "idle";
    },
    clearWebcamRecording() {
      this.form.video_consent_document = null;
      this.onInput();
    },
    cleanup() {
      if (this.recordedUrl) {
        URL.revokeObjectURL(this.recordedUrl);
      }
      this.recordedUrl = "";
      this.recordedBlob = null;
      this.recordedChunks = [];
      // Clear the video element src since it stays in DOM with v-show
      var video = this.$refs.recordedVideo;
      if (video) {
        video.removeAttribute("src");
        video.load();
      }
    },
  },
  beforeMount() {
    this.form = this.value;
  },
  beforeDestroy() {
    this.stopStream();
    clearInterval(this.recordingTimer);
    this.cleanup();
  },
};
</script>

<style scoped>
.webcam-container {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 10px;
  background: #f8f9fa;
}
.webcam-video {
  width: 100%;
  max-width: 480px;
  height: auto;
  border-radius: 4px;
  background: #000;
}
.recorded-video {
  width: 100%;
  max-width: 480px;
  height: auto;
  border-radius: 4px;
}
</style>
